import { isNodeJS } from "../compat";
import { isWebGPUSupported, isWebGPUAvailable } from "../wgs/wgpu/Support";
import { useWebGPU, enableUnorm16GeometryQuantization, skipSparseAttributeDefinitions } from "../wgs/globals";
import { registerRcsFileLoader } from "../file-loaders/rcs/RcsFileLoader";

import { Prefs3D, Prefs } from "./PreferenceNames";
import { Preferences } from "./Preferences";
import { Profile } from "./Profile";

/**
 * @callback FeatureFlagChangeListener
 * @param {boolean} value - The new value of the feature flag.
 * @private
 */

/**
 * @typedef {Object} FeatureFlagInitializationData
 * @property {*} [preferenceOverrideValue] - If provided, the value should be used for initialization instead of the preference value.
 * @property {Preferences} preferences - Shared global preferences.
 * @private
 */

/**
 * @callback FeatureFlagInitializationCallback
 * @param {boolean} value - The feature flag value.
 * @param {FeatureFlagInitializationData} initializationData - Data provided to the initialization callback.
 * @returns {Promise<boolean>} True if the initialization was successful, otherwise false.
 * @private
 */

/**
 * Static class that manages feature flags. Feature flags enable or expose capabilities in the viewer. After
 * initialization, the flags become immutable. We are currently using two types of feature flags:
 * - enabling a functionality: checked in the code to toggle/modify/patch certain functionality
 * - exposing a capability: checked in the code to expose a capability to the user, with the actual capability not
 *   being enabled until the user explicitly enables it
 * Additionally,initialization callbacks can be used to verify client support or automatically enable a feature.
 *
 * @example
 * FeatureFlags.set('ENABLE_FEATURE', true);
 * FeatureFlags.registerInitializationCallback('EXPOSE_FEATURE', (enable, initializationData) => { ... });
 *
 * @alias Autodesk.Viewing.FeatureFlags
 */
export class FeatureFlags {

  static #flagsByName = new Map();
  static #initializationPromise = null;

  /**
   * Adds a new feature flag. If a flag with that name already exists, adding is skipped.
   *
   * @param {string} name
   * @param {boolean} enable - Default value of the feature flag. True for enabled, false for disabled.
   *
   * @private
   */
  static _add(name, enable) {
    if (this.#initializationPromise) {
      console.warn(`Feature Flags: Cannot add '${name}' after global initialization.`);
      return;
    }
    if (this.#flagsByName.has(name)) {
      console.warn(`Feature Flags: Cannot add '${name}' because it already exists.`);
      return;
    }
    this.#flagsByName.set(name, {
      enabled: enable,
      initializationCallback: undefined, // FeatureFlagInitializationCallback
      changeListener: undefined // FeatureFlagChangeListener
    });
  }

  /**
   * Registers a callback to be executed on global initialization during Autodesk.Viewing.Initializer execution.
   * The callback receives the feature flag value, global preferences, and initialization data (optional). If the
   * flag was not added before, nothing happens. The registration of callbacks is only allowed before initialization
   * and is intended for flags to run initialization code. The callbacks are executed asynchronously in the order
   * the flags were added.
   *
   * @param {string} name - Feature flag identifier
   * @param {FeatureFlagInitializationCallback} callback - Initialization callback.
   *
   * @private
   */
  static _registerInitializationCallback(name, callback) {
    const flag = this.#flagsByName.get(name);
    if (flag === undefined) {
      console.warn(`Feature Flags: Cannot register initialization callback for '${name}' because it does not exist.`);
      return;
    }
    flag.initializationCallback = callback;
  }

  /**
   * Registers a callback to be executed when the feature flag is changed. The callback receives the new value of the
   * feature flag. If the flag does not exist, nothing happens.
   *
   * @param {string} name - Feature flag identifier
   * @param {FeatureFlagChangeListener} callback - Change callback to be executed when the flag is changed.
   *
   * @private
   */
  static _registerChangeListener(name, callback) {
    const flag = this.#flagsByName.get(name);
    if (flag === undefined) {
      console.warn(`Feature Flags: Cannot register change listener for '${name}' because it does not exist.`);
      return;
    }
    flag.changeListener = callback;
  }

  /**
   * Sets the value of a given feature flag. If the flag does not exist, nothing happens. If feature flags are
   * already initialized, the change listener is not called.
   *
   * @param {string} name - Feature flag identifier
   * @param {boolean} enable - New value of the feature flag
   *
   * @alias Autodesk.Viewing.FeatureFlags.set
   */
  static set(name, enable) {
    const flag = this.#flagsByName.get(name);
    if (flag === undefined) {
      console.warn(`Feature Flags: Cannot modify '${name}' because it does not exist.`);
      return;
    }
    if (this.#initializationPromise) {// initialization already started or finished
      console.warn(`Feature Flags: Cannot modify '${name}' after initialization (immutable).`);
      return;
    }
    if (flag.enabled === enable) return;

    flag.enabled = enable;
    flag.changeListener && flag.changeListener(flag.enabled);
  }

  /**
   * Returns whether a given feature flag is enabled.
   * @param {string} name - Feature flag identifier
   * @returns {boolean|undefined} Feature flag enabled state or undefined if the flag does not exist.
   *
   * @alias Autodesk.Viewing.FeatureFlags.isEnabled
   */
  static isEnabled(name) {
    const flag = this.#flagsByName.get(name);
    return flag?.enabled;
  }

  static _setInitializationData(name, initializationData) {
    const flag = this.#flagsByName.get(name);

    if (flag === undefined) {
      console.warn(`Feature Flags: Cannot set initialization data for '${name}' because it does not exist.`);
      return;
    }

    if (this.#initializationPromise) {
      console.warn(`Feature Flags: Cannot set initialization data for '${name}' because it has already been initialized.`);
      return;
    }

    flag.initializationData = initializationData;
  }

  static _getInitializationData(name) {
    const flag = this.#flagsByName.get(name);

    if (flag === undefined) {
      console.warn(`Feature Flags: Cannot get initialization data for '${name}' because it does not exist.`);
      return;
    }

    return flag.initializationData;
  }

  /**
   * Initializes all feature flags in the global context independent of a viewer instance. This method is intended to
   * be called once within the global Autodesk.Viewing.Initializer only, not anywhere else.
   *
   * @private
   */
  static async _initialize() {
    if (this.#initializationPromise) return this.#initializationPromise;

    // Prepare default profile settings and preferences.
    const defaultProfile = new Profile(Autodesk.Viewing.ProfileSettings.Default);
    const preferences = new Preferences();
    defaultProfile.apply(preferences);

    // For each flag, check availability and run initialization callback if available.
    const initializationPromises = new Array();
    for (const [, flag] of this.#flagsByName) {
      Object.freeze(flag);

      const initializationCallback = flag.initializationCallback;
      if (initializationCallback) {
        initializationPromises.push(initializationCallback(flag.enabled, { preferences, initializationData: flag.initializationData }));
      }
    }

    this.#initializationPromise = Promise.all(initializationPromises);
    return this.#initializationPromise;
  }

  /**
   * Prints all public feature flags and their values to the console
   *
   * @alias Autodesk.Viewing.FeatureFlags.print
   */
  static print() {
    console.log(`Feature Flags:${this.#flagsByName.size === 0 ? ' none' : ''}`);
    for (const [name, { enabled }] of this.#flagsByName) {
      if (Object.values(PublicFeatureFlags).includes(name)) {
        console.log(`  - ${name}: ${enabled}`);
      }
    }
  }
}


/**
 * Features in early development, internal and not intended to be exposed to any autodesk namespace.
 *
 * @todo: With the current approach, it is not always clear whether a flag is a direct toggle or a capability flag.
 * To mitigate this, introduce a stricter naming convention, e.g.,
 * - No prefix: Direct toggle (flag on = feature active)
 * - Expose*: Capability flag (flag on = option appears, user must enable)
 * - Enforce*: Policy flag (flag on = behavior forced/overridden)
 *
 * @private
 */
export const InternalFeatureFlags = Object.freeze({

  CivilToolsInIssues: 'CIVIL_TOOLS_IN_ISSUES',
  CivilToolsVerticalSegmentsEnabled: 'CIVIL_TOOLS_VERTICAL_SEGMENTS_ENABLED',
  CivilToolsPerpendicularSlopeMeasurementEnabled: 'CIVIL_TOOLS_PERPENDICULAR_SLOPE_MEASUREMENT_ENABLED',
  EnableDeleteAllMeasurements: 'ENABLE_DELETE_ALL_MEASUREMENTS',
  EnableFpsTargetSetting: 'DEPRECATED',
  EnableGPUMemorySetting: 'ENABLE_GPU_MEMORY_SETTING',
  EnableHyperModelingEdit: 'ENABLE_HYPERMODELING_EDIT',
  EnableManualAlignmentAdjustments: 'ENABLE_MANUAL_ALIGNMENT_ADJUSTMENTS',
  EnableNewHyperModelingAnchorPoints: 'ENABLE_NEW_HYPERMODELING_ANCHOR_POINTS',
  EnableLaserMeasurement: 'ENABLE_LASER_MEASUREMENT',
  EnableSurfaceMeasurement: 'ENABLE_SURFACE_MEASUREMENT',
  EnableShortestDistanceMeasurement: 'ENABLE_SHORTEST_DISTANCE_MEASUREMENT',
  EnableSmartSection: 'ENABLE_SMARTSECTION',
  EnableSlopeMeasurement: 'ENABLE_SLOPE_MEASUREMENT',
  EnableSecondaryUnits: 'ENABLE_MEASURE_SECONDARY_UNITS',
  LargeModelExperienceMobile: 'DEPRECATED',
  PropertyHash64: 'PROPERTY_HASH_64',
  EnableCameraOffset: 'ENABLE_CAMERA_OFFSET',
  AlignmentServiceUpAxisUnit: 'ALIGNMENT_SERVICE_UP_AXIS_UNIT',
  EnableWboitRendering: 'ENABLE_WBOIT_RENDERING', // Weighted Blended Order-Independent Transparency
  WebGPUPrism: 'ENABLE_WEBGPU_PRISM', // Full PrismMaterialWebGPU instead of fallback
  ModelRelativeSheetAlignment: 'ENABLE_MODEL_RELATIVE_SHEET_ALIGNMENT',
  ShowExperimentalFeatures: 'SHOW_EXPERIMENTAL_FEATURES',
  Unorm16GeometryQuantization: 'ENABLE_UNORM16_GEOMETRY_QUANTIZATION',
  EnablePointCloud: 'ENABLE_POINT_CLOUD',

  // Remove after V7 exits support window
  EnforceLargeModelExperience: 'ENFORCE_LARGE_MODEL_EXPERIENCE',
  OutOfCoreGpuMemoryManagement: 'OUT_OF_CORE_GPU_MEMORY_MANAGEMENT',
  SceneAPI: 'SCENE_API',
  V8_Preview: 'V8_PREVIEW',
  WebGLUBOs: 'WEBGL_UBOS',
  WebGPUBackend: 'ENABLE_WEBGPU',
  CopyOverlayDepth: 'COPY_OVERLAY_DEPTH',
  // this is a temporary exit hatch for files with a high number of sparse attribute definitions
  // (e.g. >300k in some IFC files).
  // longer term it may make sense to enable configuration of filter patterns in the viewer settings
  // or a similar UI instead.
  SkipSparseAttributeDefinitions: 'SKIP_SPARSE_ATTRIBUTE_DEFINITIONS'
});

/**
 * List of publicly available feature flags.
 *
 * Naming convention:
 * - No prefix: Direct toggle (flag on = feature active)
 * - Expose*: Capability flag (flag on = option appears, user must enable)
 */
export const PublicFeatureFlags = Object.freeze({
  DSEndpoints: 'DS_ENDPOINTS',
  LargeModelExperience: 'LARGE_MODEL_EXPERIENCE',
  // Remove after V7 exits support window
  EnableSpotMeasurement: 'DEPRECATED',
  LargeModelExperienceOptOut: 'DEPRECATED',
  SlowGpuDetection: 'SLOW_GPU_DETECTION',
  ModelCustomization: 'SCENE_API'
});


FeatureFlags._add(InternalFeatureFlags.V8_Preview, false);
FeatureFlags._add(InternalFeatureFlags.SceneAPI, false);

FeatureFlags._registerInitializationCallback(InternalFeatureFlags.V8_Preview, async (enabled) => {
  if (!enabled) return;

  console.log('V8 preview mode enabled');
  FeatureFlags.set(PublicFeatureFlags.LargeModelExperience, true);
  FeatureFlags.set(InternalFeatureFlags.EnforceLargeModelExperience, true);
  FeatureFlags.set(InternalFeatureFlags.WebGPUBackend, true);
  FeatureFlags.set(InternalFeatureFlags.SceneAPI, true);
  FeatureFlags.set(InternalFeatureFlags.CopyOverlayDepth, true);
});

FeatureFlags._add(InternalFeatureFlags.EnforceLargeModelExperience, false);

/**
 * This function implements/applies a standard product-controlled rollout pattern:
 * 1. updates DefaultSettings to reflect the rollout value (new default)
 * 2. cleans up localStorage if user's stored value matches the new default
 * 3. returns the effective value: user's explicit choice wins, otherwise rollout default
 *
 * @param {Preferences} preferences - The preferences instance
 * @param {string} prefName - The preference name, e.g., Prefs.WEBGPU
 * @param {boolean} rollout - The rollout flag value, true = feature on, false = feature off
 * @returns {boolean} The effective preference value to use
 *
 * @private
 */
function applyRollout(preferences, prefName, rollout) {
  const prefInLocalStorage = preferences.getPrefFromLocalStorage(prefName);
  // Cleanup: if user's stored value matches the rollout default, remove it from storage
  if (prefInLocalStorage !== undefined && prefInLocalStorage === rollout) {
    preferences.deleteFromWebStorage(prefName);
  }
  Autodesk.Viewing.DefaultSettings[prefName] = rollout;
  return prefInLocalStorage ?? rollout;
}

FeatureFlags._add(PublicFeatureFlags.LargeModelExperience, true);
FeatureFlags._registerInitializationCallback(PublicFeatureFlags.LargeModelExperience, async (enabled, { preferences, initializationData }) => {
  if (isNodeJS()) return; // Doesn't work in Node, skip initialization

  // Apply rollout logic: user's explicit preference wins, otherwise use feature flag as default
  let enable = initializationData?.overridePreferenceValue ?? applyRollout(preferences, Prefs3D.LARGE_MODEL_EXPERIENCE, enabled);
  if (FeatureFlags.isEnabled(InternalFeatureFlags.EnforceLargeModelExperience)) {
    enable = true;
  }
  if (!enable) return;

  Autodesk.Viewing.Private.enableOutOfCoreTileManager();

  // Skip this if WebGPU is not enabled. Otherwise the feature flag initialization fallback in Viewer3D will not work.
  if (FeatureFlags.isEnabled(InternalFeatureFlags.WebGPUBackend)) {
    await webGPUFeatureFlagPromise; // enableUBOs needs to know if WebGPU was enabled or not.
  }
  return Autodesk.Viewing.Private.enableUBOs();
});


// Laser measurement introduction, disabled by default
FeatureFlags._add(InternalFeatureFlags.EnableLaserMeasurement, false);

// Surface measurement introduction, disabled by default
FeatureFlags._add(InternalFeatureFlags.EnableSurfaceMeasurement, false);

// Shortest distance measurement introduction, disabled by default
FeatureFlags._add(InternalFeatureFlags.EnableShortestDistanceMeasurement, false);

// Slope measurement introduction, disabled by default
FeatureFlags._add(InternalFeatureFlags.EnableSlopeMeasurement, false);

// Camera offset to increase numerical stability in large scenes, disabled by default
FeatureFlags._add(InternalFeatureFlags.EnableCameraOffset, false);

// Encode OTG vertices as UNORM16 (positions quantized, normals oct16-packed in W). Requires WebGPU.
FeatureFlags._add(InternalFeatureFlags.Unorm16GeometryQuantization, false);
FeatureFlags._registerInitializationCallback(InternalFeatureFlags.Unorm16GeometryQuantization, async (enabled) => {
  if (!enabled) {
    return;
  }
  await webGPUFeatureFlagPromise;
  enableUnorm16GeometryQuantization();
});


// Promise to signal the WebGPU Feature Flag callback has completed execution.
let webGPUFeatureFlagPromise_resolve;
const webGPUFeatureFlagPromise = new Promise((resolve, _reject) => {
  webGPUFeatureFlagPromise_resolve = resolve;
});

/**
 * Initialization data for the WebGPUBackend feature flag.
 *
 * @typedef {Object} WebGPUInitializationData
 * @property {boolean} [overridePreferenceValue] - Developer/testing override (bypassing all rollout logic)
 * @property {boolean} [rollout] - Product-controlled rollout flag for phased feature rollout
 * @property {boolean} [forceCompatibility] - Force WebGPU compatibility mode
 * @property {GPURequestAdapterOptions} [adapterOptions] - Options passed to navigator.gpu.requestAdapter()
 * @property {Iterable<string>} [deviceFeatures] - Features to request when creating the GPU device
 *
 * @private
 */

FeatureFlags._add(InternalFeatureFlags.WebGPUBackend, false);
FeatureFlags._registerInitializationCallback(InternalFeatureFlags.WebGPUBackend, async (enabled, { preferences, initializationData }) => {
  if (!enabled) {
    // This needs to resolve no matter what! Otherwise code waiting for the promise will block forever.
    webGPUFeatureFlagPromise_resolve();
    return;
  }

  // Two distinct parameters:
  // - overridePreferenceValue: Developer/testing override, bypasses all logic (forces WebGPU on/off)
  // - rollout: Product-controlled rollout flag for phased feature rollout, sets the default
  const overrideValue = initializationData?.overridePreferenceValue;
  const rollout = initializationData?.rollout;

  let isWanted;
  if (overrideValue != null) {
    // Developer override takes priority (for testing, bypasses rollout logic entirely)
    isWanted = overrideValue;
  } else if (rollout != null) {
    // Apply rollout logic: user's explicit preference wins, otherwise use rollout as default
    isWanted = applyRollout(preferences, Prefs.WEBGPU, rollout);
  } else {
    // No rollout flag, respect user's stored preference
    isWanted = preferences.get(Prefs.WEBGPU) ?? false;
  }
  // bypassBrowserCheck: allows products to skip browser/OS validation and only check if navigator.gpu exists.
  const isSupported = initializationData?.bypassBrowserCheck ? await isWebGPUAvailable() : await isWebGPUSupported();

  const success = isWanted && isSupported && (await useWebGPU(
    initializationData?.adapterOptions,
    initializationData?.deviceFeatures,
    initializationData?.forceCompatibility));

  // If WebGPU was wanted but initialization failed, update the preference to reflect the actual state.
  // This ensures the settings panel shows WebGPU as disabled, while still allowing the user to try again.
  if (isWanted && !success) {
    preferences.set(Prefs.WEBGPU, false);
  }
  webGPUFeatureFlagPromise_resolve();
});

FeatureFlags._add(InternalFeatureFlags.ModelRelativeSheetAlignment, true);

// Improved Sectioning, disabled by default
FeatureFlags._add(InternalFeatureFlags.EnableSmartSection, false);

// Improved Secondary Units in Measurement Tool, disabled by default
FeatureFlags._add(InternalFeatureFlags.EnableSecondaryUnits, false);

// Delete all measurements functionality, disabled by default
// NOTE: This is a temporary feature flag to rollback the changes made in https://git.autodesk.com/A360/firefly.js/pull/9643
FeatureFlags._add(InternalFeatureFlags.EnableDeleteAllMeasurements, false);

// Hypermodeling Edit, disabled by default
FeatureFlags._add(InternalFeatureFlags.EnableHyperModelingEdit, false);

// Manual alignment adjustments, disabled by default
FeatureFlags._add(InternalFeatureFlags.EnableManualAlignmentAdjustments, false);

// New Hypermodeling Anchor Points, disabled by default
FeatureFlags._add(InternalFeatureFlags.EnableNewHyperModelingAnchorPoints, false);

FeatureFlags._add(PublicFeatureFlags.DSEndpoints, false);
FeatureFlags._registerInitializationCallback(PublicFeatureFlags.DSEndpoints, async (enabled) => {
  if (enabled) {
    return Autodesk.Viewing.Private.useDSEndpoints(true);
  }
});

FeatureFlags._add(InternalFeatureFlags.EnableGPUMemorySetting, false);
FeatureFlags._add(InternalFeatureFlags.CivilToolsInIssues, false);
FeatureFlags._add(InternalFeatureFlags.CivilToolsVerticalSegmentsEnabled, false);
FeatureFlags._add(InternalFeatureFlags.CivilToolsPerpendicularSlopeMeasurementEnabled, false);

FeatureFlags._add(InternalFeatureFlags.PropertyHash64, false);
FeatureFlags._registerInitializationCallback(InternalFeatureFlags.PropertyHash64, async (enabled) => {
  return Autodesk.Viewing.Private.propHash64Enabled(Boolean(enabled));
});

// Alignment service upAxis/unit overrides via Nucleus backend, disabled until backend is deployed
FeatureFlags._add(InternalFeatureFlags.AlignmentServiceUpAxisUnit, false);

FeatureFlags._add(InternalFeatureFlags.CopyOverlayDepth, false);
FeatureFlags._registerInitializationCallback(InternalFeatureFlags.CopyOverlayDepth, async (enabled) => {
  if (!enabled) {
    return;
  }
  await webGPUFeatureFlagPromise;
  Autodesk.Viewing.Private.enableOverlayDepthCopy();
});

// Full WebGPU Prism materials (PrismMaterialWebGPU) instead of MeshPhong fallback.
// Requires WebGPU. Controlled by products via:
//   FeatureFlags.set(InternalFeatureFlags.WebGPUPrism, true);
FeatureFlags._add(InternalFeatureFlags.WebGPUPrism, false);

// Order-independent transparency via WBOIT. Requires WebGPU. Controlled by products via:
//   FeatureFlags.set(InternalFeatureFlags.EnableWboitRendering, true);
FeatureFlags._add(InternalFeatureFlags.EnableWboitRendering, false);
FeatureFlags._registerInitializationCallback(InternalFeatureFlags.EnableWboitRendering, async (enabled) => {
  if (!enabled) return;

  await webGPUFeatureFlagPromise;
  Autodesk.Viewing.Private.enableWboit();
});

// Experimental settings tab in the viewer settings panel. Disabled by default.
FeatureFlags._add(InternalFeatureFlags.ShowExperimentalFeatures, false);

// Filter out attribute definitions whose category contains embedded numerics (e.g., per-instance categories).
// Reduces attribute count for models with many sparse attributes. Disabled by default.
FeatureFlags._add(InternalFeatureFlags.SkipSparseAttributeDefinitions, false);
FeatureFlags._registerInitializationCallback(InternalFeatureFlags.SkipSparseAttributeDefinitions, async (enabled) => {
  return skipSparseAttributeDefinitions(Boolean(enabled));
});

// Slow GPU detection — off by default; opt-in per product
FeatureFlags._add(PublicFeatureFlags.SlowGpuDetection, false);

// Point cloud (RCS) support, disabled by default. When enabled, registers the
// RCS FileLoader so `.rcs` files can be loaded via viewer.loadModel().
FeatureFlags._add(InternalFeatureFlags.EnablePointCloud, false);
FeatureFlags._registerInitializationCallback(InternalFeatureFlags.EnablePointCloud, async (enabled) => {
  if (!enabled) return;
  registerRcsFileLoader();
});